import type {
  EstimationItemResponse,
  EstimationModelResponse,
  MethodologyResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationItemStatus, ApiIsCalculatedMode } from '@/api/schema/data-contracts';
import type { EstimationItemForCalculation } from '@/utils/calculations/model';
import { isTemporaryId } from '@/utils/generators';
import { sortExpressions } from '@/utils/sorting';

import type { EstimationItemsFormModelRow } from '../../model';

// Stands in for a calculation ref that cannot be resolved (missing methodology or model item).
// It matches no formula token, so the item calculates as 0 instead of crashing the tab.
const UNRESOLVED_CALCULATION_REF = '';

/**
 * Extracts data needed for client-side estimate calculation
 * @param estimationItems
 * @param estimationModel
 * @param methodologies
 * @param estimationMethodologyId
 * @param formModel
 * @returns
 */
export const getEstimationItemsForCalculation = (
  estimationItems: EstimationItemResponse[],
  estimationModel: EstimationModelResponse,
  methodologies: MethodologyResponse[],
  estimationMethodologyId: string,
  formModelEstimationItems: EstimationItemsFormModelRow[],
): EstimationItemForCalculation[] => {
  const estimationItemsForCalculation: EstimationItemForCalculation[] = estimationItems
    .map((estimationItem) => {
      if (!formModelEstimationItems) {
        throw new Error('Form model estimation items cannot be undefined');
      }

      const formModelItem = formModelEstimationItems.find((x) => x.id === estimationItem.id);

      const estimationModelItem = estimationItem.estimationModelItemId
        ? estimationModel.items.find(
            (modelItem) => modelItem.id === estimationItem.estimationModelItemId,
          )
        : undefined;
      const isUsingEstimationModelForCalculation =
        estimationModelItem?.isCalculated === ApiIsCalculatedMode.FORMULA ||
        !!estimationModelItem?.calculationFormula ||
        estimationModelItem?.parameters.length;
      const methodologyId = isUsingEstimationModelForCalculation
        ? undefined
        : formModelItem
          ? (formModelItem.methodologyId ?? estimationMethodologyId)
          : (estimationItem.methodologyId ?? estimationMethodologyId);

      const methodology = methodologyId
        ? methodologies.find((m) => m.id === methodologyId)
        : undefined;

      if (methodologyId && !methodology) {
        // The item names a methodology that is not in the loaded collection, which holds exactly
        // the ones this estimation's model links. Every parameter lookup below used to assert
        // non-null and take the whole tab down with "Cannot read properties of undefined
        // (reading 'defaultParameters')" - a converted estimation in TEST did precisely that on
        // 2026-08-18. EstimationCalculator already degrades here (EWM-597); so does this now: the
        // item keeps its values and is treated as unestimated until a linked methodology is
        // reassigned.
        // eslint-disable-next-line no-console
        console.warn(
          `[getEstimationItemsForCalculation] Methodology id "${methodologyId}" not found for estimation item "${estimationItem.name}" (id: ${estimationItem.id}). ` +
            'Its parameters cannot be mapped to calculation refs; the item is treated as unestimated. ' +
            'Reassign a methodology the estimation model links to restore its estimate.',
        );
      }

      let parameters;
      if (estimationModelItem?.isCalculated === ApiIsCalculatedMode.FORMULA) {
        parameters = estimationModelItem.parameters.map((estimationModelItemParameter) => ({
          calculationRef: estimationModelItemParameter.calculationRef,
          value: estimationModelItemParameter.defaultValue ?? null,
        }));
      } else if (formModelItem) {
        parameters = formModelItem.parameters // some stale incomplete items might stay there. // related to react-hook-form that during form reset if size of parameters collection changes to smaller, // We have to filter out parameters that have no parameterName as there's a hard to simulate bug
          .filter((formModelItemParameter) => !!formModelItemParameter.parameterName)
          .map((formModelItemParameter, index) => ({
            calculationRef: isUsingEstimationModelForCalculation
              ? (estimationModelItem?.parameters.sort(sortExpressions.byOrder)[index]
                  ?.calculationRef ?? UNRESOLVED_CALCULATION_REF)
              : (methodology?.defaultParameters.sort(sortExpressions.byOrder)[index]
                  ?.calculationRef ?? UNRESOLVED_CALCULATION_REF),
            value: formModelItemParameter.value ?? null,
          }));
      } else {
        parameters = estimationItem.parameters.map((estimationItemParameter) => ({
          calculationRef: !isUsingEstimationModelForCalculation
            ? (methodology?.defaultParameters.find(
                (methodologyParameter) =>
                  methodologyParameter.id === estimationItemParameter.methodologyParameterId,
              )?.calculationRef ?? UNRESOLVED_CALCULATION_REF)
            : (estimationModelItem?.parameters.find(
                (modelItemParameter) =>
                  modelItemParameter.id === estimationItemParameter.estimationModelItemParameterId,
              )?.calculationRef ?? UNRESOLVED_CALCULATION_REF),
          value: estimationItemParameter.value ?? null,
        }));
      }

      const estimationItemForCalculation: EstimationItemForCalculation = {
        id: estimationItem.id,
        name: estimationItem.name,
        isCalculated: estimationModelItem?.isCalculated ?? ApiIsCalculatedMode.NONE,
        isOmitted: formModelItem
          ? !formModelItem.isIncluded
          : estimationItem.status === ApiEstimationItemStatus.OMITTED,
        modelItemId: estimationItem.estimationModelItemId ?? undefined,
        isParentBasedCalculation: estimationItem.isParentBasedCalculation,
        parentId: estimationItem.parentId ?? undefined,
        calculationRef: estimationModelItem?.calculationRef ?? null,
        methodologyId: methodologyId,
        parameters: parameters,
      };
      return estimationItemForCalculation;
    })
    // temporarily added custom unsaved yet items
    .concat(
      formModelEstimationItems
        ?.filter((x) => isTemporaryId(x.id))
        .map((formModelTemporaryItem) => {
          if (formModelTemporaryItem.methodologyId === undefined) {
            throw new Error('Custom estimation items are always based on methodology.'); // no estimation model item means methodology is the only calculation path; inheriting parent's model item would create implicit coupling and complicate parameter resolution
          }
          const methodologyId = formModelTemporaryItem.methodologyId!;
          const methodology = methodologies.find((m) => m.id === methodologyId)!;

          const estimationItemForCalculation: EstimationItemForCalculation = {
            id: formModelTemporaryItem.id,
            name: formModelTemporaryItem.name,
            isCalculated: ApiIsCalculatedMode.NONE,
            isOmitted: !formModelTemporaryItem.isIncluded,
            modelItemId: undefined,
            isParentBasedCalculation: true, // because it cannot have sub-items when it's not saved yet
            parentId: formModelTemporaryItem.parentId ?? undefined,
            calculationRef: null,
            methodologyId: methodologyId, // always based on methodology
            parameters: formModelTemporaryItem.parameters.map((param, paramIndex) => ({
              calculationRef: methodology.defaultParameters.sort(sortExpressions.byOrder)[
                paramIndex
              ].calculationRef,
              value: param.value,
            })),
          };
          return estimationItemForCalculation;
        }) ?? [],
    );

  return estimationItemsForCalculation;
};
